import { useEffect, useRef } from 'react';
import { io } from 'socket.io-client';
import logger from "../utils/logger";

const useWhatsappSocket = (clientId, onQr, onAuthenticated, onError, onPairingCode) => {
    const socketRef = useRef(null);
    const currentClientId = useRef(null);
    const isAuthenticatedRef = useRef(false);

    // Usar refs para callbacks para evitar reconexiones
    const onQrRef = useRef(onQr);
    const onAuthenticatedRef = useRef(onAuthenticated);
    const onErrorRef = useRef(onError);
    const onPairingCodeRef = useRef(onPairingCode);

    // Actualizar refs cuando cambien los callbacks (sin reconectar)
    useEffect(() => {
        onQrRef.current = onQr;
        onAuthenticatedRef.current = onAuthenticated;
        onErrorRef.current = onError;
        onPairingCodeRef.current = onPairingCode;
    }, [onQr, onAuthenticated, onError, onPairingCode]);

    useEffect(() => {
        if (!clientId) return;

        // Si ya tenemos socket para este cliente, no hacer nada
        if (socketRef.current && currentClientId.current === clientId) {
            return;
        }


        // 🔥 IMPORTANTE: Desconectar socket anterior si cambia el clientId
        if (socketRef.current && currentClientId.current && currentClientId.current !== clientId) {
            socketRef.current.disconnect();
            socketRef.current = null;
            isAuthenticatedRef.current = false;
        }

        // Actualizar el clientId actual
        currentClientId.current = clientId;

        // Conectar al WebSocket
        const socket = io(process.env.REACT_APP_SOCKET || 'http://localhost:4000', {
            transports: ['websocket', 'polling'],
            reconnection: true,
            reconnectionDelay: 1000,
            reconnectionAttempts: 5
        });

        socketRef.current = socket;

        socket.on('connect', () => {
            socket.emit('join', { clientId });
        });

        socket.on('disconnect', (reason) => {
            isAuthenticatedRef.current = false;
        });

        // Escuchar eventos usando refs
        socket.on('qr', ({ qr }) => {
            if (onQrRef.current) onQrRef.current(qr);
        });

        socket.on('pairing_code', ({ code }) => {
            if (onPairingCodeRef.current) onPairingCodeRef.current(code);
        });

        socket.on('authenticated', () => {
            isAuthenticatedRef.current = true;
            if (onAuthenticatedRef.current) onAuthenticatedRef.current();
        });

        socket.on('error', ({ error }) => {
            logger.error('❌ Error recibido por WebSocket:', error);
            if (onErrorRef.current) onErrorRef.current(error);
        });

        // Limpiar al desmontar
        return () => {
            if (socketRef.current) {
                socketRef.current.disconnect();
                socketRef.current = null;
            }
            currentClientId.current = null;
            isAuthenticatedRef.current = false;
        };
    }, [clientId]);

    return socketRef.current;
};

export default useWhatsappSocket;